import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { List } from "../schema/list";

export interface CampaignEditor {
	userId: number;
	userName: string;
	socketId: string;
	joinedAt: string;
}

interface CampaignEditorsUpdate {
	listId: number;
	campaignId: string;
	editors: CampaignEditor[];
}

interface SocketContextType {
	socket: Socket | null;
	listData?: List;
	editors: CampaignEditor[];
	clearActualData: () => void;
}

export interface EmailDataUpdate {
	prospectId: number;
	email: string;
	status: string;
}

export enum MessageSocketAction {
	JOIN_CAMPAIGN = "join-campaign",
	LEAVE_CAMPAIGN = "leave-campaign",
	CAMPAIGN_DATA = "campaign-data",
	CAMPAIGN_EDITORS_UPDATED = "campaign-editors-updated",
	ALL_DATA = "all-data",
	ERROR = "error",
}

const CampaignSocketContext = createContext<SocketContextType | undefined>(undefined);

export const CampaignSocketProvider = ({
	listId,
	campaignId,
	children,
}: {
	listId: number;
	campaignId: string;
	children: React.ReactNode;
}) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [listData, setListData] = useState<List>();
	const [editors, setEditors] = useState<CampaignEditor[]>([]);
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;

	const clearActualData = () => {
		setListData(undefined);
		setEditors([]);
	};

	useEffect(() => {
		// Clear stale data when campaign/list changes so CampaignPage
		// falls back to RTK Query cache until fresh socket data arrives
		setListData(undefined);
		setEditors([]);

		if (!token || !auth.user?.company?.id) {
			return;
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;

		newSocket.on("connect", () => {
			newSocket.emit(MessageSocketAction.JOIN_CAMPAIGN, {
				listId,
				campaignId,
			});
			hasLoggedError = false;
			console.info("Socket connected");
		});

		newSocket.on("connect_error", (error) => {
			if (!hasLoggedError) {
				console.info("Socket connection error:", error);
				hasLoggedError = true;
			}
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("Socket disconnected:", reason);
		});

		setSocket(newSocket);

		// Listen for campaign data updates
		newSocket.on(MessageSocketAction.CAMPAIGN_DATA, (data: List) => {
			setListData(data);
		});

		// Listen for editor updates
		newSocket.on(MessageSocketAction.CAMPAIGN_EDITORS_UPDATED, (data: CampaignEditorsUpdate) => {
			if (data.listId === listId && data.campaignId === campaignId) {
				setEditors(data.editors);
			}
		});

		// Listen for errors
		newSocket.on(MessageSocketAction.ERROR, (error: string) => {
			console.error("Socket error:", error);
		});

		return () => {
			// Leave campaign editing room before disconnecting
			newSocket.emit(MessageSocketAction.LEAVE_CAMPAIGN, {
				listId,
				campaignId,
			});
			newSocket.disconnect();
		};
	}, [token, auth.user?.company?.id, listId, campaignId]);

	return (
		<CampaignSocketContext.Provider
			value={{
				socket,
				listData,
				editors,
				clearActualData,
			}}
		>
			{children}
		</CampaignSocketContext.Provider>
	);
};

export const useCampaignSocket = () => {
	const context = useContext(CampaignSocketContext);

	if (!context) {
		throw new Error("useCampaignSocket must be used within a CampaignSocketProvider");
	}

	return context;
};
